import type { KeyboardEvent } from 'react';
import { Icon } from '../ui.js';
import { num } from '../../lib/format.js';
import {
  EDGES,
  LANES,
  MARGIN,
  NODES,
  NODE_H,
  NODE_W,
  PITCH,
  VIEW_H,
  VIEW_W,
  edgeVolume,
  metric,
  nodeById,
  nodeX,
} from './model.js';
import type { Active, EdgeDef, FlowData, Kind, NodeDef } from './model.js';

const BACK_Y = 528;

/** Tloušťka čáry z objemu (logaritmicky, aby malé toky nezmizely). */
const width = (v: number, max: number): number =>
  v <= 0 ? 1.5 : 2 + 7 * (Math.log1p(v) / Math.log1p(Math.max(max, 1)));

function edgePath(e: EdgeDef): { d: string; tip: string } {
  const a = nodeById(e.from);
  const b = nodeById(e.to);
  if (e.back) {
    const x1 = nodeX(a) + NODE_W / 2;
    const x2 = nodeX(b) + NODE_W / 2;
    const y0 = a.cy + NODE_H / 2;
    const y1 = b.cy + NODE_H / 2;
    return {
      d: `M ${x1} ${y0} V ${BACK_Y - 20} Q ${x1} ${BACK_Y} ${x1 - 20} ${BACK_Y} H ${x2 + 20} Q ${x2} ${BACK_Y} ${x2} ${BACK_Y - 20} V ${y1 + 6}`,
      tip: `${x2},${y1} ${x2 - 4.5},${y1 + 8} ${x2 + 4.5},${y1 + 8}`,
    };
  }
  if (a.lane === b.lane) {
    const cx = nodeX(a) + NODE_W / 2;
    const y0 = a.cy + NODE_H / 2;
    const y1 = b.cy - NODE_H / 2;
    return {
      d: `M ${cx} ${y0} V ${y1 - 6}`,
      tip: `${cx},${y1} ${cx - 4.5},${y1 - 8} ${cx + 4.5},${y1 - 8}`,
    };
  }
  const ax = nodeX(a) + NODE_W;
  const bx = nodeX(b);
  const dx = (bx - ax) / 2;
  return {
    d: `M ${ax} ${a.cy} C ${ax + dx} ${a.cy}, ${bx - dx - 6} ${b.cy}, ${bx - 6} ${b.cy}`,
    tip: `${bx},${b.cy} ${bx - 8},${b.cy - 4.5} ${bx - 8},${b.cy + 4.5}`,
  };
}

export function Diagram({
  data,
  kind,
  active,
  selected,
  onSelect,
  onHover,
  registerNode,
}: {
  data: FlowData;
  kind: Kind;
  active: Active | null;
  selected: string | null;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
  registerNode: (id: string, el: SVGGElement | null) => void;
}): JSX.Element {
  const volumes = new Map(EDGES.map((e) => [e.id, edgeVolume(e, data)]));
  const max = Math.max(...volumes.values(), 1);

  const key = (id: string) => (ev: KeyboardEvent<SVGGElement>) => {
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      onSelect(id);
    }
  };

  return (
    <svg
      className="fm-svg"
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      role="group"
      aria-label="Schéma toku dat zleva doprava: zdroje, příjem, cesty, plánovač, rozhodnutí, šablona, odeslání, SmartEmailing, reakce"
    >
      {LANES.map((l, i) => (
        <text
          key={l}
          className="fm-lane"
          x={MARGIN + i * PITCH + NODE_W / 2}
          y={18}
          textAnchor="middle"
        >
          {l}
        </text>
      ))}

      {EDGES.map((e) => {
        const { d, tip } = edgePath(e);
        const dim = active ? !active.edges.has(e.id) : false;
        const v = volumes.get(e.id) ?? 0;
        const w = width(v, max);
        const cls = `fm-edge k-${e.kind}${dim ? ' dim' : ''}${e.soon ? ' soon' : ''}${e.back ? ' back' : ''}`;
        return (
          <g key={e.id} className={cls} aria-hidden="true" data-edge={e.id}>
            <path className="fm-line" d={d} strokeWidth={w} />
            {v > 0 && !dim ? (
              <path className="fm-dots" d={d} strokeWidth={Math.max(w, 3.5)} />
            ) : null}
            <polygon className="fm-tip" points={tip} />
            {e.back && e.label ? (
              <text
                className="fm-edge-label"
                x={MARGIN + PITCH * 4 + NODE_W / 2}
                y={BACK_Y + 20}
                textAnchor="middle"
              >
                {e.label} · připravuje se
              </text>
            ) : null}
          </g>
        );
      })}

      {NODES.map((n) => (
        <Node
          key={n.id}
          n={n}
          kind={kind}
          data={data}
          dim={active ? !active.nodes.has(n.id) : false}
          selected={selected === n.id}
          onSelect={onSelect}
          onHover={onHover}
          onKey={key(n.id)}
          reg={registerNode}
        />
      ))}
    </svg>
  );
}

function Node({
  n,
  kind,
  data,
  dim,
  selected,
  onSelect,
  onHover,
  onKey,
  reg,
}: {
  n: NodeDef;
  kind: Kind;
  data: FlowData;
  dim: boolean;
  selected: boolean;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
  onKey: (ev: KeyboardEvent<SVGGElement>) => void;
  reg: (id: string, el: SVGGElement | null) => void;
}): JSX.Element {
  const x = nodeX(n);
  const y = n.cy - NODE_H / 2;
  const m = metric(n.id, data, kind);
  const name = n.title.filter(Boolean).join(' ');
  return (
    <g
      ref={(el) => reg(n.id, el)}
      className={`fm-node k-${n.kind}${dim ? ' dim' : ''}${selected ? ' sel' : ''}`}
      transform={`translate(${x} ${y})`}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-label={`${name}: ${num(m.value)} ${m.caption}${n.soon ? ', připravuje se' : ''}. Zobrazit podrobnosti.`}
      onClick={() => onSelect(n.id)}
      onKeyDown={onKey}
      onMouseEnter={() => onHover(n.id)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(n.id)}
      onBlur={() => onHover(null)}
      data-node={n.id}
    >
      <title>{n.sub}</title>
      <rect className="fm-card" width={NODE_W} height={NODE_H} rx={12} />
      <rect className="fm-bar" width={4} height={NODE_H - 24} x={0} y={12} rx={2} />
      <g transform="translate(11 9)" className="fm-ic">
        <Icon name={n.icon} size={16} />
      </g>
      <text className="fm-title" x={34} y={22}>
        {n.title[0]}
      </text>
      {n.title[1] ? (
        <text className="fm-title" x={34} y={36}>
          {n.title[1]}
        </text>
      ) : null}
      <text className="fm-sub" x={12} y={55}>
        {n.sub}
      </text>
      <text className="fm-num" x={12} y={81}>
        {num(m.value)}
      </text>
      <text className="fm-cap" x={12} y={94}>
        {m.caption}
      </text>
      {n.soon ? (
        <g className="fm-soon" transform={`translate(${NODE_W - 74} -9)`}>
          <rect width={68} height={17} rx={8.5} />
          <text x={34} y={12} textAnchor="middle">
            připravuje se
          </text>
        </g>
      ) : null}
    </g>
  );
}
